import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";
import { site } from "../data/siteConfig";

const navLinkClass = ({ isActive }) =>
  `text-[11px] font-semibold uppercase tracking-[0.25em] transition-colors duration-300 hover:text-white ${
    isActive ? "text-hotpink" : "text-white/60"
  }`;

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img
              src={site.assets.logoMark}
              alt="HairByYaya monogram logo"
              className="h-10 w-10 rounded-full ring-1 ring-hotpink/50"
            />
            <span className="leading-tight">
              <span className="block font-serif text-sm font-bold tracking-[0.2em]">
                HAIR BY YAYA
              </span>
              <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50">
                Hampton, VA
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {site.nav.map((n) => (
              <NavLink key={n.to} to={n.to} className={navLinkClass}>
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="header-book-now"
              className="rounded-full bg-hotpink px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-magenta"
            >
              Book Now
            </Link>
          </nav>

          <button
            className="p-2 lg:hidden"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] flex flex-col bg-ink/95 backdrop-blur-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            data-testid="mobile-menu"
          >
            <div className="flex h-16 items-center justify-between px-5">
              <span className="font-script text-2xl text-hotpink">Yaya</span>
              <button
                className="p-2"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
              {[...site.nav, { label: "Book Now", to: "/book" }].map((n, i) => (
                <motion.div
                  key={n.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.45 }}
                >
                  <Link
                    to={n.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={`block border-b border-white/10 py-4 font-serif text-3xl ${
                      location.pathname === n.to ? "text-hotpink" : "text-white"
                    } ${n.to === "/book" ? "italic text-hotpink" : ""}`}
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <motion.a
                href={site.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 flex items-center gap-2 text-sm text-white/60"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Instagram className="h-4 w-4 text-hotpink" />
                {site.instagram.handle}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

